import React from 'react'

// ==================== ENUMS ====================

export enum UserRole {
  EMPLOYER = 'employer',
  AGENT = 'agent',
  SUPER_ADMIN = 'super_admin',
}

export enum UserStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  SUSPENDED = 'SUSPENDED',
}

export enum InvestigationStatus {
  NEW = 'new',
  PENDING = 'pending',
  AWAITING_APPROVAL = 'awaiting_approval',
  IN_PROGRESS = 'in_progress',
  UNDER_REVIEW = 'under_review',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
}

export enum CheckType {
  STANDARD = 'standard',
  ENHANCED = 'enhanced',
  PRO = 'pro',
}

export enum InvestigationState {
  DETAILS_SUBMITTED = 'details_submitted',
  INVESTIGATION_REPORT = 'investigation_report',
}

export enum PaymentStatus {
  PENDING = 'PENDING',
  SUCCESSFUL = 'SUCCESSFUL',
  FAILED = 'FAILED',
  REFUNDED = 'REFUNDED',
}

export enum DocumentType {
  CV = 'cv',
  NATIONAL_ID = 'national_id',
  IBIN = 'nin',
  PASSPORT = 'passport',
  AGENT_REPORT = 'agent_report',
  INVESTIGATION_REPORT = 'investigation_report',
  OTHER = 'other',
}

export enum DocumentApprovalStatus {
  AWAITING_APPROVAL = 'awaiting_approval',
  APPROVED = 'approved',
  REJECTED = 'rejected',
  COMPLETED = 'completed',
}

export enum DocumentUploader {
  EMPLOYEE = 'employee',
  AGENT = 'agent',
}

export enum RiskLevel {
  LOW = 'low',
  MODERATE = 'moderate',
  HIGH = 'high',
  SEVERE = 'severe',
  CRITICAL = 'critical',
}

export enum RiskRecommendation {
  PROCEED = 'proceed',
  PROCEED_WITH_CONDITIONS = 'proceed_with_conditions',
  ESCALATE_BOARD_REVIEW = 'escalate_board_review',
  DO_NOT_PROCEED = 'do_not_proceed',
}

export enum RiskCategory {
  IDENTITY_INTEGRITY = 'identity_integrity',
  CRIMINAL_ANTI_CORRUPTION = 'criminal_anti_corruption',
  FINANCIAL_DIRECTORSHIP = 'financial_directorship',
  REGULATORY_DISCIPLINE = 'regulatory_discipline',
  POLITICAL_EXPOSURE = 'political_exposure',
  LITIGATION_HISTORY = 'litigation_history',
  SAFEGUARDING_CONDUCT = 'safeguarding_conduct',
  REPUTATIONAL_MEDIA = 'reputational_media',
  CONFLICT_OF_INTEREST = 'conflict_of_interest',
  GUARANTOR_RELIABILITY = 'guarantor_reliability',
}

export enum EarningStatus {
  PENDING = 'PENDING',
  PAID = 'PAID',
}

export enum WaitlistStatus {
  PENDING = 'pending',
  CONTACTED = 'contacted',
  CONVERTED = 'converted',
}

export enum TicketStatus {
  OPEN = 'open',
  IN_PROGRESS = 'in_progress',
  RESOLVED = 'resolved',
  CLOSED = 'closed',
}

export enum TicketPriority {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  URGENT = 'urgent',
}

export enum TicketCategory {
  TECHNICAL = 'technical',
  BILLING = 'billing',
  ACCOUNT = 'account',
  INVESTIGATION = 'investigation',
  GENERAL = 'general',
  OTHER = 'other',
}

// ==================== USER TYPES ====================

export interface User {
  id: string
  email: string
  firstName: string
  lastName: string
  phoneNumber?: string
  role: UserRole
  status: UserStatus
  isPhoneVerified: boolean
  isEmailVerified: boolean
  companyName?: string
  country?: string
  state?: string
  city?: string
  gender?: 'male' | 'female' | 'other'
  createdAt: string
  updatedAt: string
}

export interface UserStatistics {
  totalInvestigations: number
  completedInvestigations: number
  totalSpent?: number // for Employer
  totalEarnings?: number // for Agent
}

export interface UserWithStats extends User {
  statistics: UserStatistics
}

// ==================== AUTH TYPES ====================

export interface LoginRequest {
  email?: string
  phoneNumber?: string
}

export interface LoginResponse {
  success: boolean
  message: string
  phoneNumber: string
  email: string
}

export interface LoginVerifyRequest {
  email?: string
  phoneNumber?: string
  otp: string
}

export interface LoginVerifyResponse {
  access_token: string
  refresh_token: string
  user: User
}

export interface RegisterRequest {
  email: string
  firstName: string
  lastName: string
  phoneNumber: string
  role: UserRole
  companyName?: string
  country?: string
  state?: string
  city?: string
  gender?: 'male' | 'female' | 'other'
}

export interface RegisterResponse {
  message: string
  user: User
  requiresPhoneVerification: boolean
}

export interface RefreshTokenRequest {
  refreshToken: string
}

export interface RefreshTokenResponse {
  access_token: string
  refresh_token: string
}

export interface SendOtpRequest {
  phoneNumber: string
}

export interface SendOtpResponse {
  message: string
  expiresIn: number
}

export interface VerifyOtpRequest {
  phoneNumber: string
  otp: string
}

export interface VerifyOtpResponse {
  success: boolean
  message: string
  access_token: string
  refresh_token: string
  user: User
}

// ==================== INVESTIGATION TYPES ====================

export interface Investigation {
  id: string
  employeeFirstName: string
  employeeMiddleName?: string
  employeeLastName: string
  employeeEmail: string
  employeePhone: string
  employeeGender?: string
  checkType: CheckType
  status: InvestigationStatus
  requestLink: string
  isFormSubmitted: boolean
  employeeConsentGiven?: boolean
  dateConsentGiven?: string
  dateOfBirth?: string
  nationality?: string
  stateOfOrigin?: string
  localGovernmentArea?: string
  usa?: string
  iin?: string
  dateRequested?: string
  dateSubmitted?: string
  reportSubmittedDate?: string
  investigationNotes?: string
  reportUrl?: string
  riskScore?: number
  // Questionnaire Step 1: Personal Identification
  fullLegalName?: string
  previousNames?: string
  placeOfBirth?: string
  otherNationalities?: string
  nationalIdentificationNumber?: string
  addressHistory?: string // JSON string
  workHistory?: string // JSON string
  educationHistory?: string // JSON string
  // Questionnaire Step 2: Criminal & Law Enforcement
  hasConviction?: string
  convictionDetails?: string
  isUnderInvestigation?: string
  investigationDetails?: string
  hasFinancialCrimeCharge?: string
  financialCrimeDetails?: string
  hasPleaBargain?: string
  pleaBargainDetails?: string
  hasBeenDisengaged?: string
  disengagementDetails?: string
  // Questionnaire Step 3: Financial & Directorship
  hasLiquidatedCompany?: string
  liquidatedCompanyDetails?: string
  hasDirectorDisqualification?: string
  directorDisqualificationDetails?: string
  isCurrentDirector?: string
  currentDirectorshipDetails?: string
  // Questionnaire Step 4: Regulatory & Professional Discipline
  hasDisciplinaryProceedings?: string
  disciplinaryProceedingsDetails?: string
  hasLicenceRevoked?: string
  licenceRevokedDetails?: string
  revokedLicences?: string // JSON string
  hasRegulatoryBodySanction?: string
  regulatoryBodySanctionDetails?: string
  // Questionnaire Step 5: Political Exposure
  isPep?: string
  hasHeldPublicOffice?: string
  publicOfficeDetails?: string
  isRelatedToPep?: string
  hasProcurementInvestigation?: string
  // Questionnaire Step 6: Safeguarding & Workplace Conduct
  hasMisconductDismissal?: string
  misconductDismissalDetails?: string
  hasWorkplaceInvestigation?: string
  hasWorkedWithVulnerableGroups?: string
  hasLeadershipBar?: string
  leadershipBarDetails?: string
  // Questionnaire Step 7: Litigation & Civil Claims
  hasShareholderDisputes?: string
  hasJudgments?: string
  // Questionnaire Step 8: Reputational & Media
  hasAdverseMedia?: string
  adverseMediaDetails?: string
  hasPublicAccusation?: string
  hasUndisclosedReputationalMatters?: string
  // Questionnaire Step 9: Conflict of Interest
  hasBusinessConflict?: string
  businessConflictDetails?: string
  hasProcurementRelationship?: string
  procurementRelationshipDetails?: string
  hasCompetingBoard?: string
  competingBoardDetails?: string
  // Consent fields
  consentCriminalBackground?: boolean
  consentEmploymentReference?: boolean
  consentGuarantorReference?: boolean
  consentAddressVerification?: boolean
  consentSocialMediaReview?: boolean
  consentCourtRecordSearches?: boolean
  consentLifestyleAudit?: boolean
  consentProcurementIntegrityChecks?: boolean
  // Risk Assessment results
  totalRiskScore?: number
  maxRiskScore?: number
  riskScorePercentage?: number
  riskLevel?: RiskLevel
  riskRecommendation?: RiskRecommendation
  autoRejectReason?: string
  riskAssessments?: RiskCategoryScore[]
  riskAssessmentVersion?: number
  lastRiskAssessmentAt?: string | null
  isReportValidated?: boolean
  reportValidatedBy?: string
  reportValidatedAt?: string
  validationNotes?: string
  earningCreated?: boolean
  earningId?: string
  isPaid?: boolean
  amountPaid?: number
  serviceCharge?: number
  employerId: string
  agentId?: string
  employer?: User
  agent?: User
  documents?: InvestigationDocument[]
  earning?: Earning
  createdAt: string
  updatedAt: string
}

export interface CreateInvestigationRequest {
  checkType: CheckType
  employeeFirstName: string
  employeeMiddleName?: string
  employeeLastName: string
  employeeEmail: string
  employeePhone: string
  employeeGender?: string
  transactionId?: string // Optional - link to existing successful payment for payment-first flow
  referenceNumber?: string
  description?: string
  urgencyLevel?: 'low' | 'medium' | 'high'
  requiresBackgroundCheck?: boolean
}

// Server returns the Investigation flat (with `requestLink` as a sibling field on
// the Investigation interface itself). The baseQuery unwraps `{ success, data, timestamp }`
// before RTK Query sees it, so `mutation.data` is the Investigation directly.
export type CreateInvestigationResponse = Investigation

export interface SubmitEmployeeFormRequest {
  dateOfBirth: string
  nationality: string
  stateOfOrigin: string
  localGovernmentArea: string
  usa?: string
  iin?: string
}

export interface AssignAgentRequest {
  agentId: string
}

export interface SubmitReportRequest {
  investigationNotes: string
  reportUrl: string
}

export interface InvestigationStats {
  total: number
  new: number
  pending: number
  in_progress: number
  completed: number
  cancelled: number
  averageCompletionDays?: number
  completionRate?: number
}

// ==================== CHECK OPTIONS / PRICING TYPES ====================

export interface CheckOption {
  id: string
  name: string
  price: number
  currency: string
  popular: boolean
  features: string[]
}

// ==================== RISK ASSESSMENT TYPES ====================

export interface ScoringCriteriaOption {
  score: number
  label: string
}

export type ScoringCriteriaResponse = Record<string, ScoringCriteriaOption[]>

export interface RiskCategoryScore {
  id?: string
  category: string
  rawScore: number
  selectedCriteria: string
  rationale?: string
  evidenceReferences?: string
  weight?: number
  weightedScore?: number
  scoredById?: string
  createdAt?: string
  updatedAt?: string
  version?: number
}

export interface RiskAssessmentSubmission {
  categoryScores: Pick<RiskCategoryScore, 'category' | 'rawScore' | 'selectedCriteria' | 'rationale' | 'evidenceReferences'>[]
  investigationNotes: string
}

export interface RiskAssessmentResponse {
  totalScore: number
  maxScore: number
  percentage: number
  riskLevel: RiskLevel
  recommendation: RiskRecommendation
  autoRejectReason?: string | null
  version?: number
  categoryScores: RiskCategoryScore[]
}

export interface RiskAssessmentHistoryResponse {
  success: boolean
  data: RiskAssessmentResponse[]
}

export interface DocumentApprovalRequest {
  status: 'approved' | 'rejected' | 'completed'
  notes?: string
}

// ==================== DOCUMENT TYPES ====================

export interface InvestigationDocument {
  id: string
  investigationId: string
  documentType: DocumentType
  fileName: string
  fileUrl: string
  downloadUrl?: string
  fileSize: number
  mimeType: string
  uploadedBy?: DocumentUploader
  approvalStatus?: DocumentApprovalStatus
  approvedById?: string
  approvalNotes?: string
  approvedAt?: string
  uploadedAt: string
  updatedAt?: string
}

export interface UploadDocumentResponse {
  id: string
  investigationId: string
  documentType: DocumentType
  fileName: string
  fileUrl: string
  fileSize: number
  mimeType: string
  uploadedAt: string
}

export interface DocumentValidationResponse {
  isValid: boolean
  missingDocuments: string[]
  uploadedDocuments: {
    type: DocumentType
    uploaded: boolean
    fileName?: string
  }[]
}

// ==================== PAYMENT TYPES ====================

export interface InitiateFlutterwavePaymentRequest {
  checkType: CheckType
  redirectUrl?: string
  investigationId?: string
}

export interface InitiateFlutterwavePaymentResponse {
  paymentLink: string
  txRef: string
  transactionId: string
  amount: number
  currency?: string
  provider?: string
}

export interface VerifyFlutterwavePaymentRequest {
  tx_ref: string
}

export interface VerifyFlutterwavePaymentResponse {
  success: boolean
  transactionId: string
  status: 'successful' | 'failed' | 'cancelled' | string
  amount: number
  checkType: CheckType
}

export interface Transaction {
  id: string
  transactionId: string
  investigationId: string | null // Can be null if payment made but not yet linked to investigation
  checkType: CheckType // Added to support payment-first flow
  amount: number
  currency: string
  status: PaymentStatus
  paymentMethod?: string
  datePaid?: string // Added to match API response
  createdAt: string
  updatedAt: string
  investigation?: {
    id: string
    employeeFirstName: string
    employeeLastName: string
  }
}

export interface ReusablePayment {
  transactionId: string
  checkType: CheckType
  amount: number
  status: PaymentStatus
  datePaid: string
  investigationId: string | null
}

// ==================== EARNINGS TYPES ====================

export interface Earning {
  id: string
  agentId: string
  investigationId: string
  amount: number
  amountEarned?: string
  employeeName?: string
  checkType?: CheckType | string
  status: EarningStatus
  dateSubmitted?: string
  datePaid?: string
  createdAt: string
  updatedAt?: string
  paidAt?: string
  agent?: User
  investigation?: Investigation
}

export interface EarningsSummary {
  totalEarnings: number
  paidEarnings: number
  pendingEarnings: number
  totalCount: number
  paidCount: number
  pendingCount: number
}

export interface EarningsBreakdown {
  total: number
  paid: number
  pending: number
  byCheckType: {
    checkType: string
    total: number
    count: number
  }[]
}

export interface PlatformEarningsStats {
  totalEarnings: number
  paidEarnings: number
  pendingEarnings: number
  totalCount: number
  paidCount: number
  pendingCount: number
  averageEarningPerInvestigation: number
}

export interface MarkEarningPaidResponse {
  id: string
  status: EarningStatus
  paidAt: string
  message: string
}

export interface BulkMarkPaidRequest {
  earningIds: string[]
}

export interface BulkMarkPaidResponse {
  updatedCount: number
  message: string
}

// ==================== ANALYTICS TYPES ====================

export interface DashboardStats {
  investigations: {
    total: number
    ongoing: number
    completed: number
    pending: number
    new: number
  }
  financial: {
    totalRevenue?: number
    totalEarnings?: number
    pendingPayments?: number
    totalSpent?: number
    platformProfit?: number
  }
  users?: {
    totalEmployers: number
    totalAgents: number
    activeUsers: number
  }
}

export interface InvestigationTrend {
  date: string
  count: number
  status: string
}

export interface RevenueTrend {
  date: string
  revenue: number
  count: number
}

export interface TopAgent {
  agentId: string
  firstName: string
  lastName: string
  completedInvestigations: number
  avgCompletionDays: number
}

export interface StatusDistribution {
  status: string
  count: number
}

export interface FinancialStats {
  totalRevenue?: number
  totalSpent?: number
  totalEarnings?: number
  paidEarnings?: number
  pendingEarnings?: number
  platformProfit?: number
  averageTransactionAmount?: number
  averageEarningPerInvestigation?: number
  transactionCount?: number
}

// ==================== ADMIN TYPES ====================

export interface InviteAgentRequest {
  email: string
  firstName: string
  lastName: string
  phoneNumber: string
}

export interface UpdateUserStatusRequest {
  status: UserStatus
}

export interface ReassignInvestigationRequest {
  agentId: string
}

export interface PlatformStatistics {
  users: {
    total: number
    employers: number
    agents: number
    active: number
  }
  investigations: {
    total: number
    completed: number
    completionRate: string
  }
  financial: {
    totalRevenue: number
    totalEarnings: number
    platformProfit: number
  }
}

// ==================== WAITLIST TYPES ====================

export interface WaitlistEntry {
  firstName: string
  lastName: string
  email: string
  phoneNumber?: string
  notes?: string
}

export interface WaitlistResponse {
  id: number
  firstName: string
  lastName: string
  email: string
  phoneNumber?: string
  status: WaitlistStatus
  notes?: string
  createdAt: string
  updatedAt: string
}

export interface WaitlistStats {
  total: number
  pending: number
  contacted: number
  converted: number
}

export interface CheckEmailResponse {
  exists: boolean
  status?: WaitlistStatus
}

export interface UpdateWaitlistRequest {
  firstName?: string
  lastName?: string
  email?: string
  phoneNumber?: string
  status?: WaitlistStatus
  notes?: string
}

export interface BulkUpdateWaitlistRequest {
  ids: number[]
  status: WaitlistStatus
}

// ==================== COMMON API RESPONSE TYPES ====================

export interface ApiResponse<T = unknown> {
  data?: T
  message?: string
  success?: boolean
  errors?: string[]
}

export interface ApiError {
  message: string
  status: number
  response?: unknown
}

export interface PaginatedResponse<T> {
  data: T[]
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
  }
}

// ==================== FORM TYPES ====================

export interface FormField {
  name: string
  label: string
  type: 'text' | 'email' | 'password' | 'number' | 'textarea' | 'select'
  required?: boolean
  placeholder?: string
  options?: { value: string; label: string }[]
}

// ==================== NAVIGATION TYPES ====================

export interface NavItem {
  id: string
  label: string
  path: string
  icon?: React.ComponentType<{ className?: string }>
  roles?: UserRole[]
  children?: NavItem[]
}

// ==================== SUPPORT TICKET TYPES ====================

export interface SupportTicket {
  id: string
  userId: string
  subject: string
  message: string
  category: TicketCategory
  status: TicketStatus
  priority: TicketPriority
  assignedToId?: string
  adminResponse?: string
  resolvedAt?: string
  resolvedById?: string
  createdAt: string
  updatedAt: string
  user?: User
  assignedTo?: User
  resolvedBy?: User
}

export interface CreateTicketRequest {
  subject: string
  message: string
  category?: TicketCategory
}

export interface UpdateTicketStatusRequest {
  status: TicketStatus
  adminResponse?: string
}

export interface UpdateTicketPriorityRequest {
  priority: TicketPriority
}

export interface AssignTicketRequest {
  adminId: string
}

export interface TicketStatistics {
  totalTickets: number
  openTickets: number
  inProgressTickets: number
  resolvedTickets: number
  closedTickets: number
  urgentTickets: number
  activeTickets: number
}

// ==================== BANK ACCOUNT TYPES ====================

export interface BankAccount {
  id: string
  bankName: string
  accountNumber: string
  accountName: string
  bankCode?: string
  isDefault: boolean
  createdAt: string
  updatedAt: string
  user?: User
}

export interface AddBankAccountRequest {
  bankName: string
  accountNumber: string
  accountName: string
  bankCode?: string
  isDefault?: boolean
}

export interface UpdateBankAccountRequest {
  bankName?: string
  accountNumber?: string
  accountName?: string
  bankCode?: string
  isDefault?: boolean
}

// ==================== WITHDRAWAL TYPES ====================

export enum WithdrawalStatus {
  PENDING = 'pending',
  APPROVED = 'approved',
  REJECTED = 'rejected',
  PROCESSING = 'processing',
  COMPLETED = 'completed',
  FAILED = 'failed',
}

export interface Withdrawal {
  id: string
  amount: number
  status: WithdrawalStatus
  reason?: string
  reviewNotes?: string
  transactionReference?: string
  reviewedBy?: string
  reviewedAt?: string
  processedAt?: string
  bankAccount: {
    id: string
    bankName: string
    accountNumber: string
    accountName: string
  }
  user?: User
  createdAt: string
  updatedAt: string
}

export interface CreateWithdrawalRequest {
  bankAccountId: string
  amount: number
  reason?: string
}

export interface ReviewWithdrawalRequest {
  status: 'approved' | 'rejected'
  reviewNotes?: string
}

export interface ProcessWithdrawalRequest {
  status: 'completed' | 'failed'
  transactionReference: string
}

export interface WithdrawalStatistics {
  totalRequests: number
  pendingRequests: number
  approvedRequests: number
  completedRequests: number
  rejectedRequests: number
  totalAmountRequested: number
  totalAmountCompleted: number
  totalAmountPending: number
}

// ==================== UTILITY TYPES ====================

export type Theme = 'light' | 'dark' | 'system'
export type Optional<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>
export type RequiredFields<T, K extends keyof T> = T & Required<Pick<T, K>>
